import { BallStatusMsgType } from 'lib_ts/enums/machine-msg.enum';
import { IBallStatusMsg } from 'lib_ts/interfaces/machine-msg/i-ball-status';
import { create } from 'zustand';

// machine throttles drop ball requests after two attempts within 10 seconds and stops
// providing status updates, so the lock needs a timeout backstop
export const DROP_BALL_COOLDOWN_SEC = 15;

let cooldownTimeout: NodeJS.Timeout | undefined;

type DropBallStateSlice = {
  droppingBall: boolean;

  // whether the caller wants a success/failure toast
  // * IMPORTANT: outlives droppingBall so the result handler can still read it
  notifyResult: boolean;
};

type DropBallActionSlice = {
  /** result indicates whether the lock was acquired or not */
  readonly beginDropBall: (notifyResult: boolean) => boolean;

  // for callers that opt out of the lock but still have a toast preference
  readonly setNotifyResult: (notifyResult: boolean) => void;

  /** releases the lock only for a status that resolves the request, see resolvesDropBall */
  readonly handleBallStatus: (msg: IBallStatusMsg) => void;

  readonly endDropBall: () => void;
};

type DropBallStore = DropBallStateSlice & DropBallActionSlice;

/**
 * ! only an after-dropball result or a ball actually landing resolves our request
 * ! every other type (after-fire, after-send, default) streams continuously while
 * ! auto-firing through RT/BTC data collection and would otherwise cancel the cooldown
 */
const resolvesDropBall = (msg: IBallStatusMsg) =>
  msg.type === BallStatusMsgType.AfterDropball || msg.ball_count > 0;

const useDropBallStore = create<DropBallStore>((set, get) => ({
  droppingBall: false,
  notifyResult: false,

  beginDropBall: (notifyResult) => {
    if (get().droppingBall) {
      return false;
    }

    clearTimeout(cooldownTimeout);
    cooldownTimeout = setTimeout(
      () => get().endDropBall(),
      DROP_BALL_COOLDOWN_SEC * 1_000
    );

    set({ droppingBall: true, notifyResult });
    return true;
  },

  setNotifyResult: (notifyResult) => set({ notifyResult }),

  handleBallStatus: (msg) => {
    if (!get().droppingBall) {
      return;
    }

    if (!resolvesDropBall(msg)) {
      return;
    }

    get().endDropBall();
  },

  endDropBall: () => {
    clearTimeout(cooldownTimeout);
    cooldownTimeout = undefined;

    set({ droppingBall: false });
  },
}));

export default useDropBallStore;
